import React from "react";
import { Typography, TextField } from "@mui/material";

/**
 * Currency CODE ("USD", "GBP", "INR") -> display symbol ("$", "£", "₹").
 *
 * Derived from Intl rather than a hardcoded lookup table so any ISO code the
 * data carries renders correctly without this file having to know about it. The
 * fraction digits are pinned to 0 purely to make the digits easy to strip — the
 * amount itself is formatted by the caller, this only extracts the symbol.
 *
 * Falls back to "$" on an unknown/malformed code: Intl throws a RangeError for
 * anything that is not a well-formed currency code, and a money field with no
 * symbol at all reads worse than one with the wrong symbol.
 */
const currencySymbol = (code) => {
  if (!code) return "$";
  try {
    return (0)
      .toLocaleString("en-US", {
        style: "currency",
        currency: String(code).toUpperCase(),
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/[\d\s.,]/g, "")
      .trim() || "$";
  } catch {
    return "$";
  }
};

/**
 * `currency` is the ISO code for money fields (PRICE, COST). It is a prop rather
 * than a constant because the symbol used to be a hardcoded "$" applied to any
 * label containing "price" — which silently mislabelled every non-USD product,
 * and skipped COST entirely since "cost" does not contain "price".
 *
 * Callers pass the currency that belongs to THAT field: a product's selling
 * currency for PRICE, its `cost_currency` for COST. Those are separate fields on
 * the core product for a reason (see control-tower product.model.js), so this
 * component must not assume one covers the other.
 */
const ReadOnlyFieldGroup = ({ label, value, currency }) => {
  const formatValue = (val, lbl) => {
    // Deliberately `!val`, not `val == null`: preserved from the original so a
    // 0 keeps rendering as an empty field rather than "$0", which is how every
    // other blank field in this modal reads.
    if (!val) return val;

    // Money fields get a symbol and comma separators. COST is matched
    // explicitly alongside PRICE — it is the other money field on these cards.
    const key = (lbl || "").toLowerCase();
    if (key.includes("price") || key.includes("cost")) {
      const numValue = parseFloat(val);
      if (!isNaN(numValue)) {
        return `${currencySymbol(currency)}${numValue.toLocaleString("en-US")}`;
      }
    }

    return val;
  };

  return (
    <>
      <Typography fontSize="14px" fontFamily="poppins" fontWeight={400}>
        {label}
      </Typography>
      <TextField
        value={formatValue(value, label) || ""}
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        sx={{
          marginTop: 0.5,
          "& .MuiInputBase-input": { color: "var(--text-secondary)" },
          "& .MuiOutlinedInput-root": { backgroundColor: "var(--bg-input)" },
        }}
      />
    </>
  );
};

export default ReadOnlyFieldGroup;
